import { useState, useEffect, useMemo } from 'react';
import {
    Modal,
    Button,
    Badge,
    Text,
    Paper,
    Group,
    ActionIcon,
    Tooltip,
    Textarea,
    Loader,
    Tabs
} from '@mantine/core';
import {
    IconMail,
    IconSend,
    IconTrash,
    IconArrowBackUp,
    IconPaperclip,
    IconExternalLink,
    IconUser,
    IconDownload,
    IconEye,
    IconFileText,
    IconAlertCircle,
    IconBrandGoogle
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import api from '@/services/api-service';
import { notify } from '@/lib/notify';
import { SETTINGS_GMAIL_PATH } from '@/lib/navigation';
import {
    useReservationEmailThreads,
    useReservationEmailThreadDetails,
    useUnlinkReservationEmailThread,
    useReplyReservationEmailThread
} from '@/hooks/api-hooks';
import { formatDayLong, formatToLocalTime } from '@/lib/format-utils';
import EmailMessageBody from './email-message-body';

interface ModalReservationEmailsProps {
    reservationId: number;
    opened: boolean;
    onClose: () => void;
    initialThreadId?: string | null;
}

export default function ModalReservationEmails({
    reservationId,
    opened,
    onClose,
    initialThreadId
}: ModalReservationEmailsProps): JSX.Element {
    const { t } = useTranslation();

    const { data: threadsData, isLoading: loadingThreads } = useReservationEmailThreads(reservationId);
    const rawThreads = (threadsData as any)?.data || [];

    const threads = useMemo(() => {
        if (!Array.isArray(rawThreads)) return [];
        const seenIds = new Set<string>();
        const seenSubjects = new Set<string>();
        const unique: any[] = [];

        for (const th of rawThreads) {
            const idKey = (th.gmail_thread_id || '').toLowerCase();
            const subjKey = (th.subject || '').trim().toLowerCase();

            const isDuplicateId = Boolean(idKey && seenIds.has(idKey));
            const isDuplicateSubj = Boolean(subjKey && seenSubjects.has(subjKey));

            if (isDuplicateId || isDuplicateSubj) {
                continue;
            }

            if (idKey) seenIds.add(idKey);
            if (subjKey) seenSubjects.add(subjKey);
            unique.push(th);
        }
        return unique;
    }, [rawThreads]);

    const [activeThreadId, setActiveThreadId] = useState<string | null>(initialThreadId || null);

    useEffect(() => {
        if (opened && initialThreadId) {
            setActiveThreadId(initialThreadId);
        }
    }, [opened, initialThreadId]);

    useEffect(() => {
        if (threads.length > 0 && (!activeThreadId || !threads.some((th: any) => th.gmail_thread_id === activeThreadId))) {
            const matchInitial = initialThreadId && threads.some((th: any) => th.gmail_thread_id === initialThreadId);
            setActiveThreadId(matchInitial ? initialThreadId : threads[0].gmail_thread_id);
        } else if (threads.length === 0) {
            setActiveThreadId(null);
        }
    }, [threads, activeThreadId, initialThreadId]);

    const {
        data: threadDetailsData,
        isLoading: loadingDetails,
        isError: errorLoadingDetails
    } = useReservationEmailThreadDetails(
        reservationId,
        activeThreadId || undefined
    );
    const threadDetails = (threadDetailsData as any)?.data;
    const messages = threadDetails?.messages || [];

    const unlinkMutation = useUnlinkReservationEmailThread();
    const replyMutation = useReplyReservationEmailThread();

    const [replyText, setReplyText] = useState('');
    const [replyAll, setReplyAll] = useState(false);
    const [previewImage, setPreviewImage] = useState<{ url: string; filename: string } | null>(null);
    const [downloadingAttId, setDownloadingAttId] = useState<string | null>(null);

    const handleDownloadAttachment = async (messageId: string, att: any) => {
        setDownloadingAttId(att.id);
        try {
            await api.reservations.downloadEmailAttachment(
                reservationId,
                messageId,
                att.id,
                att.filename,
                att.mimeType
            );
        } catch {
            notify.error('operation.error', 'failed_to_download_attachment');
        } finally {
            setDownloadingAttId(null);
        }
    };

    const activeThreadRecord = threads.find((t: any) => t.gmail_thread_id === activeThreadId);

    const handleUnlink = (threadId: string) => {
        unlinkMutation.mutate(
            { reservationId, threadId },
            {
                onSuccess: () => {
                    if (threads.length <= 1) {
                        onClose();
                    }
                }
            }
        );
    };

    const handleSendReply = () => {
        if (!replyText.trim() || !activeThreadId || errorLoadingDetails) return;

        const lastMessage = messages[messages.length - 1];
        const to = lastMessage?.from || activeThreadRecord?.participants?.[0]?.email || '';
        const cc = replyAll ? lastMessage?.cc : undefined;

        if (!to) {
            notify.error('operation.error', 'recipient_email_required');
            return;
        }

        replyMutation.mutate(
            {
                reservationId,
                threadId: activeThreadId,
                bodyText: replyText,
                bodyHtml: `<p>${replyText.replace(/\n/g, '<br/>')}</p>`,
                to,
                cc,
                replyAll,
                subject: activeThreadRecord?.subject || 'Re:'
            },
            {
                onSuccess: () => {
                    setReplyText('');
                }
            }
        );
    };

    return (
        <>
            <Modal
            opened={opened}
            onClose={onClose}
            size="xl"
            radius="lg"
            title={
                <Group className="gap-2">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                        <IconMail size={18} />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="font-bold text-sm text-gray-900">
                                {t('emails_and_messages')}
                            </span>
                            <Badge size="xs" color="dark" variant="filled">
                                {threads.length}
                            </Badge>
                        </div>
                        <span className="text-[11px] text-gray-400">
                            {t('reservation')} #{reservationId}
                        </span>
                    </div>
                </Group>
            }
            centered
        >
            {loadingThreads ? (
                <div className="py-12 flex justify-center items-center">
                    <Loader size="sm" color="blue" />
                </div>
            ) : threads.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                    <IconMail size={32} className="mx-auto text-gray-300 mb-2" />
                    <Text className="text-xs">{t('no_linked_emails_yet')}</Text>
                </div>
            ) : (
                <div className="space-y-4 pt-1">
                    {/* Multi-thread tabs if more than one */}
                    {threads.length > 1 && (
                        <Tabs
                            value={activeThreadId || undefined}
                            onChange={(val) => setActiveThreadId(val)}
                            variant="pills"
                            radius="md"
                        >
                            <Tabs.List className="gap-1 mb-2">
                                {threads.map((th: any) => (
                                    <Tabs.Tab
                                        key={th.gmail_thread_id}
                                        value={th.gmail_thread_id}
                                        className="text-xs"
                                    >
                                        {th.subject || t('no_subject')}
                                    </Tabs.Tab>
                                ))}
                            </Tabs.List>
                        </Tabs>
                    )}

                    {/* Active Thread Header */}
                    {activeThreadRecord && (
                        <div className="flex items-start justify-between p-3 bg-slate-50 border border-slate-200/80 rounded-xl">
                            <div className="space-y-1 min-w-0 pr-3">
                                <h3 className="font-bold text-sm text-gray-900 truncate">
                                    {activeThreadRecord.subject || t('no_subject')}
                                </h3>
                                <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
                                    {activeThreadRecord.participants?.length > 0 && (
                                        <span>
                                            {activeThreadRecord.participants.map((p: any) => p.name || p.email).join(', ')}
                                        </span>
                                    )}
                                    <span>•</span>
                                    <span>
                                        {activeThreadRecord.last_message_at
                                            ? `${formatDayLong(activeThreadRecord.last_message_at)} ${formatToLocalTime(activeThreadRecord.last_message_at)}`
                                            : ''}
                                    </span>
                                </div>
                            </div>

                            <Group className="gap-1 shrink-0">
                                <Tooltip label={t('open_in_gmail')}>
                                    <ActionIcon
                                        variant="subtle"
                                        color="gray"
                                        size="sm"
                                        component="a"
                                        href={`https://mail.google.com/mail/u/0/#inbox/${activeThreadRecord.gmail_thread_id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <IconExternalLink size={15} />
                                    </ActionIcon>
                                </Tooltip>
                                <Tooltip label={t('unlink')}>
                                    <ActionIcon
                                        variant="subtle"
                                        color="red"
                                        size="sm"
                                        onClick={() => handleUnlink(activeThreadRecord.gmail_thread_id)}
                                        loading={unlinkMutation.isPending}
                                    >
                                        <IconTrash size={15} />
                                    </ActionIcon>
                                </Tooltip>
                            </Group>
                        </div>
                    )}

                    {/* Messages List */}
                    <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                        {loadingDetails ? (
                            <div className="py-10 flex flex-col items-center justify-center gap-2 text-gray-400">
                                <Loader size="sm" color="blue" />
                                <span className="text-xs">{t('loading_thread')}</span>
                            </div>
                        ) : errorLoadingDetails ? (
                            <Paper withBorder className="p-5 rounded-xl bg-amber-50/70 border-amber-200 text-center space-y-3">
                                <div className="flex items-center justify-center gap-2 text-amber-800 font-semibold text-xs">
                                    <IconAlertCircle size={16} />
                                    <span>{t('error_loading_thread_messages')}</span>
                                </div>
                                <Text className="text-xs text-amber-700 max-w-md mx-auto">
                                    {t('google_session_expired_or_invalid_desc')}
                                </Text>
                                <Button
                                    size="xs"
                                    variant="light"
                                    color="orange"
                                    component="a"
                                    href={SETTINGS_GMAIL_PATH}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    leftSection={<IconBrandGoogle size={14} />}
                                >
                                    {t('reconnect_google_account')}
                                </Button>
                            </Paper>
                        ) : messages.length === 0 ? (
                            <Paper withBorder className="p-4 rounded-xl bg-gray-50 text-center text-xs text-gray-500">
                                {t('no_messages_in_thread')}
                            </Paper>
                        ) : (
                            messages.map((msg: any) => (
                                <Paper
                                    key={msg.id}
                                    withBorder
                                    className="p-4 rounded-xl shadow-2xs border-gray-200/80 bg-white"
                                >
                                    {/* Message Sender Header */}
                                    <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-100">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <div className="w-7 h-7 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-xs font-bold shrink-0">
                                                <IconUser size={14} />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-semibold text-xs text-gray-900 truncate">
                                                    {msg.from}
                                                </div>
                                                <div className="text-[10px] text-gray-400 truncate">
                                                    {t('recipient_to')}: {msg.to}
                                                </div>
                                            </div>
                                        </div>

                                        <span className="text-[10px] text-gray-400 shrink-0">
                                            {msg.date ? `${formatDayLong(msg.date)} ${formatToLocalTime(msg.date)}` : ''}
                                        </span>
                                    </div>

                                    {/* Message Body */}
                                    <EmailMessageBody
                                        bodyHtml={msg.bodyHtml}
                                        bodyText={msg.bodyText || msg.snippet}
                                    />

                                    {/* Attachments */}
                                    {msg.attachments?.length > 0 && (
                                        <div className="mt-3 pt-2.5 border-t border-gray-100 space-y-2">
                                            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                                                {t('attachments')} ({msg.attachments.length})
                                            </div>

                                            {/* Image Attachments Gallery */}
                                            {msg.attachments.some((att: any) => att.mimeType?.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/i.test(att.filename)) && (
                                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 my-1.5">
                                                    {msg.attachments
                                                        .filter((att: any) => att.mimeType?.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/i.test(att.filename))
                                                        .map((att: any) => (
                                                            <div
                                                                key={att.id}
                                                                className="group relative rounded-lg border border-slate-200 bg-slate-50 overflow-hidden shadow-2xs hover:shadow-xs transition-all flex flex-col"
                                                            >
                                                                {att.dataUrl ? (
                                                                    <div
                                                                        className="cursor-pointer aspect-video sm:aspect-4/3 w-full bg-slate-100 flex items-center justify-center overflow-hidden"
                                                                        onClick={() => setPreviewImage({ url: att.dataUrl, filename: att.filename })}
                                                                    >
                                                                        <img
                                                                            src={att.dataUrl}
                                                                            alt={att.filename}
                                                                            className="w-full h-full object-contain group-hover:scale-102 transition-transform duration-200"
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <div
                                                                        className="cursor-pointer aspect-video sm:aspect-4/3 w-full bg-slate-100 flex flex-col items-center justify-center p-2 text-slate-400"
                                                                        onClick={() => handleDownloadAttachment(msg.id, att)}
                                                                    >
                                                                        <IconFileText size={28} />
                                                                        <span className="text-[10px] text-gray-500 mt-1 font-medium">{t('view_attachment')}</span>
                                                                    </div>
                                                                )}

                                                                <div className="p-1.5 bg-white/95 flex items-center justify-between gap-1 border-t border-slate-100">
                                                                    <div className="min-w-0 flex-1">
                                                                        <div className="text-[11px] font-medium text-gray-700 truncate" title={att.filename}>
                                                                            {att.filename}
                                                                        </div>
                                                                        <div className="text-[9px] text-gray-400">
                                                                            {Math.round(att.size / 1024)} KB
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex items-center gap-1 shrink-0">
                                                                        {att.dataUrl && (
                                                                            <ActionIcon
                                                                                size="xs"
                                                                                variant="subtle"
                                                                                color="gray"
                                                                                title={t('view_attachment')}
                                                                                onClick={() => setPreviewImage({ url: att.dataUrl, filename: att.filename })}
                                                                            >
                                                                                <IconEye size={13} />
                                                                            </ActionIcon>
                                                                        )}
                                                                        <ActionIcon
                                                                            size="xs"
                                                                            variant="subtle"
                                                                            color="blue"
                                                                            title={t('download_attachment')}
                                                                            loading={downloadingAttId === att.id}
                                                                            onClick={() => handleDownloadAttachment(msg.id, att)}
                                                                        >
                                                                            <IconDownload size={13} />
                                                                        </ActionIcon>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                </div>
                                            )}

                                            {/* Non-image File Attachments list */}
                                            {msg.attachments.some((att: any) => !att.mimeType?.startsWith('image/') && !/\.(png|jpe?g|gif|webp|svg)$/i.test(att.filename)) && (
                                                <div className="flex flex-wrap gap-2">
                                                    {msg.attachments
                                                        .filter((att: any) => !att.mimeType?.startsWith('image/') && !/\.(png|jpe?g|gif|webp|svg)$/i.test(att.filename))
                                                        .map((att: any) => (
                                                            <button
                                                                type="button"
                                                                key={att.id}
                                                                onClick={() => handleDownloadAttachment(msg.id, att)}
                                                                disabled={downloadingAttId === att.id}
                                                                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded text-[11px] text-gray-700 cursor-pointer transition-colors"
                                                            >
                                                                {downloadingAttId === att.id ? (
                                                                    <Loader size={12} color="gray" />
                                                                ) : (
                                                                    <IconPaperclip size={12} className="text-gray-400" />
                                                                )}
                                                                <span className="font-medium truncate max-w-[180px]">{att.filename}</span>
                                                                <span className="text-gray-400 text-[9px]">
                                                                    ({Math.round(att.size / 1024)} KB)
                                                                </span>
                                                                <IconDownload size={12} className="text-gray-400 hover:text-gray-700 ml-0.5" />
                                                            </button>
                                                        ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Paper>
                            ))
                        )}
                    </div>

                    {/* Inline Reply Form */}
                    <div className="pt-2 border-t border-gray-100 space-y-2">
                        <Textarea
                            placeholder={errorLoadingDetails ? t('cannot_reply_thread_error') : t('write_reply_placeholder')}
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            minRows={2}
                            maxRows={4}
                            autosize
                            disabled={errorLoadingDetails}
                            className="text-xs"
                        />

                        <div className="flex items-center justify-between">
                            <Group className="gap-2">
                                <Button
                                    variant={replyAll ? 'filled' : 'subtle'}
                                    color="gray"
                                    size="xs"
                                    onClick={() => setReplyAll(!replyAll)}
                                    disabled={errorLoadingDetails}
                                >
                                    <IconArrowBackUp size={14} className="mr-1" />
                                    {t('reply_all')}
                                </Button>
                            </Group>

                            <Button
                                color="dark"
                                size="xs"
                                leftSection={<IconSend size={14} />}
                                onClick={handleSendReply}
                                loading={replyMutation.isPending}
                                disabled={!replyText.trim() || !activeThreadId || errorLoadingDetails}
                            >
                                {t('send_reply')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </Modal>

        {/* Image Fullscreen Preview Modal */}
        {previewImage && (
            <Modal
                opened={!!previewImage}
                onClose={() => setPreviewImage(null)}
                title={
                    <div className="flex items-center gap-2 max-w-md truncate text-xs font-semibold text-gray-800">
                        <IconPaperclip size={14} className="text-gray-400 shrink-0" />
                        <span className="truncate">{previewImage.filename}</span>
                    </div>
                }
                size="lg"
                centered
            >
                <div className="flex flex-col items-center justify-center p-2 bg-slate-50 rounded-lg">
                    <img
                        src={previewImage.url}
                        alt={previewImage.filename}
                        className="max-h-[75vh] max-w-full object-contain rounded shadow-xs"
                    />
                    <div className="mt-3 flex justify-end w-full">
                        <Button
                            size="xs"
                            variant="light"
                            color="blue"
                            leftSection={<IconDownload size={14} />}
                            onClick={() => {
                                const a = document.createElement('a');
                                a.href = previewImage.url;
                                a.download = previewImage.filename;
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                            }}
                        >
                            {t('download_attachment')}
                        </Button>
                    </div>
                </div>
            </Modal>
        )}
    </>
    );
}
